import { DemandResponseDTO } from '@luminovo/api-contracts';
import { isPresent, isWithinDayRangeInclusive } from '@luminovo/commons';

export interface DemandSummary {
    shipToSite: string;
    availableInventory: number;
    grossDemand: number;
    netDemand: number;
}

export function calculateDemandSummary({
    demands,
    startDate,
    endDate,
}: {
    demands: DemandResponseDTO[];
    startDate: Date;
    endDate: Date;
}): DemandSummary[] {
    const { summaryMap } = demands.reduce(
        ({ summaryMap, availableInventoryDate }, demand) => {
            const shipToSiteNumber = demand.ship_to_site.site_number ?? '';
            const prev = summaryMap.get(shipToSiteNumber) ?? {
                shipToSite: shipToSiteNumber,
                netDemand: 0,
                grossDemand: 0,
                availableInventory: 0,
            };

            // Only sum demands within the given date range
            if (
                isWithinDayRangeInclusive({
                    date: new Date(demand.delivery_start_date),
                    start: startDate,
                    end: endDate,
                })
            ) {
                prev.grossDemand += demand.gross_quantity.quantity;
                prev.netDemand += demand.net_quantity.quantity;
            }

            // finds the nearest delivery date in the future
            // (because availableInventory describes the inventory before the demand is delivered)
            const now = new Date();
            const deliveryDate = new Date(demand.delivery_start_date);
            if (
                !isPresent(availableInventoryDate) ||
                (deliveryDate <= now && deliveryDate > availableInventoryDate) ||
                (deliveryDate > now && availableInventoryDate < now) ||
                (deliveryDate > now && deliveryDate < availableInventoryDate)
            ) {
                prev.availableInventory = demand.inventory_quantity?.quantity ?? 0;
                availableInventoryDate = deliveryDate;
            }

            summaryMap.set(shipToSiteNumber, prev);
            return { summaryMap, availableInventoryDate };
        },

        { summaryMap: new Map<string, DemandSummary>(), availableInventoryDate: null as Date | null },
    );

    return Array.from(summaryMap.values());
}
